import { useEffect, useRef, useState } from "react";
import { locationStore, requestCurrentLocation } from "@/lib/location";
import {
  loadTencentMapSdk,
  reverseGeocode,
  searchNearbyPlaces,
  type TencentNearbyPlace,
  type TencentReverseGeocodeResult,
} from "@/lib/tencent-map";

const MAP_CONTAINER_ID = "athome-address-map-container";

export interface TencentMapPickerValue {
  address: string;
  latitude: number;
  longitude: number;
  province?: string;
  city?: string;
  district?: string;
}

interface TencentMapPickerProps {
  initialValue?: Partial<TencentMapPickerValue>;
  onConfirm: (value: TencentMapPickerValue) => void;
  onBack: () => void;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function samePoint(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }) {
  return Math.abs(a.latitude - b.latitude) < 0.00001 && Math.abs(a.longitude - b.longitude) < 0.00001;
}

function buildFallbackAddress(
  latitude: number,
  longitude: number,
  preferredAddress?: string,
) {
  const normalizedPreferredAddress = String(preferredAddress ?? "").trim();
  if (normalizedPreferredAddress) {
    return normalizedPreferredAddress;
  }
  return `地图选点 ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

function buildCurrentPlace(location: TencentReverseGeocodeResult): TencentNearbyPlace {
  return {
    id: "current-location",
    title: "当前位置",
    address: location.address,
    latitude: location.latitude,
    longitude: location.longitude,
    isCurrent: true,
  };
}

export default function TencentMapPicker({ initialValue, onConfirm, onBack }: TencentMapPickerProps) {
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const requestIdRef = useRef(0);
  const [keyword, setKeyword] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState<TencentReverseGeocodeResult | null>(null);
  const [places, setPlaces] = useState<TencentNearbyPlace[]>([]);
  const [booting, setBooting] = useState(true);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [fatalError, setFatalError] = useState("");
  const [serviceNotice, setServiceNotice] = useState("");

  const destroyMap = () => {
    try {
      mapRef.current?.destroy?.();
    } catch {
      // ignore map destroy error
    }
    mapRef.current = null;
    markerRef.current = null;
  };

  const moveMarker = (latitude: number, longitude: number, center = true) => {
    const TMap = (window as Window & { TMap?: any }).TMap;
    if (!TMap || !markerRef.current || !mapRef.current) {
      return;
    }

    const position = new TMap.LatLng(latitude, longitude);
    markerRef.current.updateGeometries([
      {
        id: "selected-address",
        styleId: "selected",
        position,
      },
    ]);

    if (center) {
      mapRef.current.setCenter(position);
    }
  };

  const initMap = async (latitude: number, longitude: number) => {
    const TMap = (await loadTencentMapSdk()) as any;
    destroyMap();

    const center = new TMap.LatLng(latitude, longitude);
    mapRef.current = new TMap.Map(MAP_CONTAINER_ID, {
      center,
      zoom: 16,
    });
    markerRef.current = new TMap.MultiMarker({
      map: mapRef.current,
      styles: {
        selected: new TMap.MarkerStyle({
          anchor: { x: 16, y: 32 },
        }),
      },
      geometries: [
        {
          id: "selected-address",
          styleId: "selected",
          position: center,
        },
      ],
    });

    mapRef.current.on("click", (event: { latLng?: { lat: number; lng: number } }) => {
      const latitudeValue = Number(event.latLng?.lat);
      const longitudeValue = Number(event.latLng?.lng);
      if (!latitudeValue || !longitudeValue) {
        return;
      }
      void selectPoint(latitudeValue, longitudeValue, true, "地图选点");
    });
  };

  const applyFallbackSelection = (
    latitude: number,
    longitude: number,
    preferredAddress: string | undefined,
    notice: string,
  ) => {
    const fallbackLocation: TencentReverseGeocodeResult = {
      address: buildFallbackAddress(latitude, longitude, preferredAddress),
      latitude,
      longitude,
    };
    setSelectedLocation(fallbackLocation);
    setPlaces([buildCurrentPlace(fallbackLocation)]);
    setServiceNotice(notice);
  };

  const selectPoint = async (
    latitude: number,
    longitude: number,
    centerMap: boolean,
    preferredAddress?: string,
  ) => {
    const currentRequestId = ++requestIdRef.current;
    setLoadingLocation(true);
    setFatalError("");
    moveMarker(latitude, longitude, centerMap);

    try {
      const result = await reverseGeocode(latitude, longitude);
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setSelectedLocation(result);
      setServiceNotice("");
    } catch (requestError) {
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      applyFallbackSelection(
        latitude,
        longitude,
        preferredAddress ?? initialValue?.address,
        getErrorMessage(requestError, "地址解析暂不可用，已保留当前选点坐标"),
      );
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoadingLocation(false);
      }
    }
  };

  const relocateToCurrentPosition = async () => {
    setFatalError("");
    try {
      const location = await requestCurrentLocation();
      moveMarker(location.latitude, location.longitude, true);
      await selectPoint(location.latitude, location.longitude, true, "当前位置");
    } catch (requestError) {
      setFatalError(getErrorMessage(requestError, "当前位置获取失败，请检查定位权限后重试"));
    }
  };

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      setBooting(true);
      setFatalError("");
      try {
        const initialLatitude = initialValue?.latitude;
        const initialLongitude = initialValue?.longitude;
        const cachedLocation = locationStore.getFresh();
        const fallbackLocation =
          cachedLocation ??
          (typeof initialLatitude === "number" && typeof initialLongitude === "number"
            ? null
            : await requestCurrentLocation());
        const resolvedLatitude =
          typeof initialLatitude === "number"
            ? initialLatitude
            : fallbackLocation?.latitude;
        const resolvedLongitude =
          typeof initialLongitude === "number"
            ? initialLongitude
            : fallbackLocation?.longitude;

        if (typeof resolvedLatitude !== "number" || typeof resolvedLongitude !== "number") {
          throw new Error("当前无法获取有效定位，请检查定位权限后重试");
        }

        if (cancelled) {
          return;
        }

        await initMap(resolvedLatitude, resolvedLongitude);
        if (cancelled) {
          return;
        }
        await selectPoint(
          resolvedLatitude,
          resolvedLongitude,
          true,
          initialValue?.address || "当前位置",
        );
      } catch (requestError) {
        if (!cancelled) {
          setFatalError(getErrorMessage(requestError, "腾讯地图加载失败，请稍后重试"));
        }
      } finally {
        if (!cancelled) {
          setBooting(false);
        }
      }
    };

    void boot();

    return () => {
      cancelled = true;
      destroyMap();
    };
  }, [initialValue?.address, initialValue?.latitude, initialValue?.longitude]);

  useEffect(() => {
    if (!selectedLocation) {
      return;
    }

    let cancelled = false;
    setLoadingPlaces(true);

    const timer = window.setTimeout(() => {
      const searchKeyword = keyword.trim();
      void searchNearbyPlaces({
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        keyword: searchKeyword,
        city: selectedLocation.city,
      })
        .then((results) => {
          if (cancelled) {
            return;
          }

          if (searchKeyword) {
            setPlaces(results);
            return;
          }

          setPlaces([
            buildCurrentPlace(selectedLocation),
            ...results.filter(
              (item) =>
                !samePoint(
                  { latitude: item.latitude, longitude: item.longitude },
                  {
                    latitude: selectedLocation.latitude,
                    longitude: selectedLocation.longitude,
                  },
                ),
            ),
          ]);
        })
        .catch((requestError) => {
          if (cancelled) {
            return;
          }
          setPlaces([buildCurrentPlace(selectedLocation)]);
          setServiceNotice((currentNotice) =>
            currentNotice ||
            getErrorMessage(requestError, "附近地址检索暂不可用，已保留当前选点坐标"),
          );
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingPlaces(false);
          }
        });
    }, keyword.trim() ? 350 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [keyword, searchTrigger, selectedLocation]);

  return (
    <div
      className="customer-fixed-shell fixed inset-y-0 z-[9999] flex w-full flex-col bg-white"
      style={{ minHeight: "100dvh" }}
    >
      <div
        className="flex flex-shrink-0 items-center gap-3 bg-white px-4 pb-3"
        style={{
          borderBottom: "1px solid #F0EAE0",
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        }}
      >
        <button
          onClick={onBack}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full cursor-pointer"
          style={{ background: "#F5F2EE" }}
        >
          <i className="ri-arrow-left-s-line text-lg" style={{ color: "#555" }} />
        </button>
        <span className="flex-1 text-center text-base font-bold" style={{ color: "#1A1A1A" }}>
          选择服务地址
        </span>
        <button
          onClick={() => void relocateToCurrentPosition()}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full cursor-pointer"
          style={{ background: "#FFF2E8" }}
        >
          <i className="ri-focus-3-line text-base" style={{ color: "#FF6018" }} />
        </button>
      </div>

      <div
        className="flex min-w-0 items-center gap-2 px-4 py-3 flex-shrink-0"
        style={{ background: "#FAFAF8", borderBottom: "1px solid #F0EAE0" }}
      >
        <div
          className="flex min-w-0 flex-1 items-center gap-2 rounded-xl px-3 py-2"
          style={{ background: "#fff", border: "1px solid #EDE8E0" }}
        >
          <i className="ri-search-line text-sm flex-shrink-0" style={{ color: "#AAAAAA" }} />
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setKeyword(keyword.trim());
                setSearchTrigger((value) => value + 1);
                event.currentTarget.blur();
              }
            }}
            placeholder="搜索小区 / 酒店 / 大厦"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
            style={{ color: "#333" }}
          />
          {keyword ? (
            <button
              onClick={() => setKeyword("")}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center cursor-pointer"
              aria-label="清空搜索"
            >
              <i className="ri-close-circle-fill text-base" style={{ color: "#CCC" }} />
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => {
            setKeyword(keyword.trim());
            setSearchTrigger((value) => value + 1);
          }}
          className="h-10 flex-shrink-0 whitespace-nowrap rounded-xl px-3 text-sm font-semibold cursor-pointer disabled:cursor-not-allowed"
          style={{
            background: keyword.trim() ? "#FF6018" : "#EEE7DE",
            color: keyword.trim() ? "#fff" : "#999",
          }}
          disabled={!keyword.trim()}
        >
          搜索
        </button>
      </div>

      <div className="relative overflow-hidden flex-shrink-0" style={{ height: "min(38vh, 280px)" }}>
        <div id={MAP_CONTAINER_ID} className="h-full w-full" />
        {booting ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{ background: "rgba(255,255,255,0.88)" }}
          >
            <i className="ri-loader-4-line mb-2 text-2xl animate-spin" style={{ color: "#FF6018" }} />
            <p className="text-sm font-semibold" style={{ color: "#555" }}>
              正在定位并加载地图...
            </p>
          </div>
        ) : null}
      </div>

      {selectedLocation ? (
        <div
          className="px-4 py-3 flex-shrink-0"
          style={{ background: "#FFF8F2", borderBottom: "1px solid #F6E6D8" }}
        >
          <p className="text-xs font-semibold" style={{ color: "#FF7A30" }}>
            当前选中地址
          </p>
          <p className="mt-1 text-sm leading-6" style={{ color: "#333" }}>
            {selectedLocation.address || initialValue?.address || "未解析到地址"}
          </p>
        </div>
      ) : null}

      {fatalError ? (
        <div
          className="mx-4 mt-3 rounded-2xl px-4 py-3 flex-shrink-0"
          style={{ background: "#FFF5F5", border: "1px solid #FFD8D8", color: "#D25B5B" }}
        >
          <div className="flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 text-sm" />
            <div className="flex-1">
              <p className="text-xs leading-relaxed">{fatalError}</p>
              <button
                onClick={() => void relocateToCurrentPosition()}
                className="mt-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer"
                style={{ background: "#fff", color: "#D25B5B" }}
              >
                重新定位
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!fatalError && serviceNotice ? (
        <div
          className="mx-4 mt-3 rounded-2xl px-4 py-3 flex-shrink-0"
          style={{ background: "#FFF8F0", border: "1px solid #FFD7B3", color: "#C76A1A" }}
        >
          <div className="flex items-start gap-2">
            <i className="ri-information-line mt-0.5 text-sm" />
            <p className="flex-1 text-xs leading-relaxed">{serviceNotice}</p>
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto" style={{ background: "#FAFAF8" }}>
        <p
          className="sticky top-0 px-4 py-2.5 text-xs font-semibold"
          style={{ color: "#999", background: "#FAFAF8", borderBottom: "1px solid #F0EAE0" }}
        >
          {keyword.trim()
            ? (loadingPlaces ? "搜索中..." : "搜索结果")
            : (loadingPlaces ? "附近地点加载中..." : "附近地址")}
        </p>
        {!loadingPlaces && places.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm" style={{ color: "#999" }}>
            未找到相关地址，请换个关键词试试
          </div>
        ) : null}
        {places.map((place) => {
          const active = selectedLocation
            ? samePoint(
                { latitude: selectedLocation.latitude, longitude: selectedLocation.longitude },
                { latitude: place.latitude, longitude: place.longitude },
              )
            : false;

          return (
            <button
              key={place.id}
              onClick={() => void selectPoint(place.latitude, place.longitude, true, place.address || place.title)}
              className="flex w-full items-start gap-3 px-4 py-3.5 text-left cursor-pointer"
              style={{
                borderBottom: "1px solid #F0EAE0",
                background: active ? "#FFF8F0" : "#fff",
              }}
            >
              <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center">
                {place.isCurrent ? (
                  <i className="ri-navigation-fill text-base" style={{ color: "#FF6018" }} />
                ) : (
                  <i
                    className="ri-map-pin-2-line text-base"
                    style={{ color: active ? "#FF6018" : "#CCCCCC" }}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {place.isCurrent ? (
                    <span
                      className="rounded px-1.5 py-0.5 text-[11px] font-bold flex-shrink-0"
                      style={{ background: "#FFF0E0", color: "#FF6018" }}
                    >
                      当前定位
                    </span>
                  ) : null}
                  <p className="text-sm font-medium" style={{ color: place.isCurrent ? "#FF6018" : "#1A1A1A" }}>
                    {place.title}
                  </p>
                </div>
                <p className="mt-0.5 text-xs" style={{ color: "#999" }}>
                  {place.address}
                </p>
              </div>
              <div className="ml-1 mt-0.5 flex flex-shrink-0 items-center gap-2">
                {!place.isCurrent && place.distanceText ? (
                  <span className="text-xs" style={{ color: "#BBBBBB" }}>
                    {place.distanceText}
                  </span>
                ) : null}
                {active ? <i className="ri-check-line text-base" style={{ color: "#FF6018" }} /> : null}
              </div>
            </button>
          );
        })}
      </div>

      <div
        className="flex-shrink-0 px-4 pt-4"
        style={{
          borderTop: "1px solid #F0EAE0",
          background: "#fff",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        }}
      >
        <button
          onClick={() => {
            if (!selectedLocation) {
              return;
            }
            onConfirm({
              address: selectedLocation.address || initialValue?.address || "",
              latitude: selectedLocation.latitude,
              longitude: selectedLocation.longitude,
              province: selectedLocation.province,
              city: selectedLocation.city,
              district: selectedLocation.district,
            });
          }}
          disabled={!selectedLocation || loadingLocation}
          className="w-full whitespace-nowrap rounded-full py-3.5 text-sm font-bold text-white cursor-pointer disabled:cursor-not-allowed"
          style={{
            background: !selectedLocation || loadingLocation ? "#F0B37D" : "linear-gradient(90deg, #FF5C00, #FF9A3C)",
            opacity: !selectedLocation || loadingLocation ? 0.8 : 1,
          }}
        >
          {loadingLocation ? "正在确认位置..." : "确认使用此地址"}
        </button>
      </div>
    </div>
  );
}
